import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  FaArrowRight,
  FaCheckCircle,
  FaChevronRight,
  FaCircle,
  FaClock,
  FaMotorcycle,
  FaMoneyBillWave,
  FaPhoneAlt,
  FaRoute,
  FaShieldAlt,
  FaStar,
  FaStore,
  FaUserCheck,
  FaWallet,
} from "react-icons/fa";

import {
  MdClose,
  MdDeliveryDining,
  MdKeyboardArrowRight,
  MdLogin,
  MdLocationOn,
  MdMenu,
  MdNotifications,
  MdPayments,
  MdSecurity,
  MdSupportAgent,
  MdVerified,
} from "react-icons/md";
import Footer from "../components/Footer";
import Headers from "../components/Headers";
import Register from "./Register";

const COLORS = {
  red: "#EF4444",
  redDark: "#DC2626",
  redDeep: "#C62828",
  redSoft: "#FFF1F2",
  redLight: "#FEF2F2",
  orange: "#F97316",
  text: "#171717",
  muted: "#737373",
  border: "#F3F4F6",
};

const benefits = [
  {
    icon: <FaMoneyBillWave />,
    title: "Attractive Earnings",
    text: "Earn from successful deliveries while working around your own schedule.",
  },
  {
    icon: <FaClock />,
    title: "Flexible Working",
    text: "Choose when you want to ride and create a schedule that works for you.",
  },
  {
    icon: <FaRoute />,
    title: "Smart Delivery",
    text: "Manage deliveries through a simple and efficient rider experience.",
  },
  {
    icon: <FaShieldAlt />,
    title: "Reliable Support",
    text: "Get dedicated support whenever you need help during your rider journey.",
  },
];

const steps = [
  {
    number: "01",
    icon: <FaUserCheck />,
    title: "Apply Online",
    text: "Complete your rider registration with a few simple steps.",
  },
  {
    number: "02",
    icon: <MdVerified />,
    title: "Get Verified",
    text: "Submit your information and complete the verification process.",
  },
  {
    number: "03",
    icon: <MdDeliveryDining />,
    title: "Start Riding",
    text: "Get delivery orders and start earning with Foodbite24.",
  },
];

const riderFeatures = [
  "Flexible working hours",
  "Easy rider dashboard",
  "Instant delivery notifications",
  "Earning tracking",
  "Order status management",
  "Dedicated rider support",
];

const Home = () => {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  const goLogin = () => {
    setMenuOpen(false);
    navigate("/login");
  };

  const goRegister = () => {
    setMenuOpen(false);
    navigate("/register");
  };

  const goHome = () => {
    setMenuOpen(false);
    navigate("/");
  };

  const scrollToSection = (id) => {
    setMenuOpen(false);

    const element = document.getElementById(id);

    if (element) {
      element.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  return (
    <main className="min-h-screen overflow-hidden bg-white text-neutral-900">
      {/* =========================================================
          NAVBAR
      ========================================================= */}
    <Headers/>
      {/* =========================================================
          HERO
      ========================================================= */}
      <section
        id="home"
        className="relative overflow-hidden bg-white pt-32 sm:pt-36 lg:pt-44"
      >
        {/* Background Shapes */}
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute -left-40 top-24 h-[420px] w-[420px] rounded-full bg-red-100/70 blur-3xl" />

          <div className="absolute right-[-180px] top-10 h-[520px] w-[520px] rounded-full bg-orange-100/60 blur-3xl" />

          <div className="absolute left-[42%] top-[25%] h-32 w-32 rounded-full bg-red-50 blur-3xl" />
        </div>

        <div className="relative mx-auto max-w-7xl px-4 pb-20 sm:px-6 lg:px-8 lg:pb-28">
          <div className="grid items-center gap-14 lg:grid-cols-[0.95fr_1.05fr] lg:gap-16">
            {/* Hero Content */}
            <div className="relative z-20">
              <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-red-100 bg-red-50 px-4 py-2 text-xs font-black uppercase tracking-[0.14em] text-red-600">
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400 opacity-75" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-red-500" />
                </span>

                Foodbite24 Rider Partner
              </div>

              <h1 className="max-w-3xl text-5xl font-black leading-[0.98] tracking-[-0.055em] text-neutral-950 sm:text-6xl lg:text-[70px] xl:text-[78px]">
                Ride on your
                <span className="block text-red-500">own terms.</span>
              </h1>

              <p className="mt-7 max-w-xl text-base leading-8 text-neutral-500 sm:text-lg">
                Join Foodbite24 as a Rider Partner and turn your free time into
                earning opportunities with flexible delivery work and a simple
                rider experience.
              </p>

              {/* CTA */}
              <div className="mt-9 flex flex-col gap-3 sm:flex-row">
                <button
                  type="button"
                  onClick={goRegister}
                  className="group relative z-[100] flex min-h-[60px] cursor-pointer items-center justify-center gap-3 rounded-2xl bg-red-500 px-7 text-sm font-black text-white shadow-[0_20px_45px_rgba(239,68,68,0.28)] transition duration-300 hover:-translate-y-1 hover:bg-red-600 active:scale-[0.98]"
                >
                  Become a Rider

                  <FaArrowRight className="transition duration-300 group-hover:translate-x-1" />
                </button>

                <button
                  type="button"
                  onClick={goLogin}
                  className="relative z-[100] flex min-h-[60px] cursor-pointer items-center justify-center gap-3 rounded-2xl border border-neutral-200 bg-white px-7 text-sm font-bold text-neutral-700 shadow-[0_10px_30px_rgba(0,0,0,0.04)] transition duration-300 hover:-translate-y-1 hover:border-red-200 hover:bg-red-50 hover:text-red-600 active:scale-[0.98]"
                >
                  <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-red-50 text-red-500">
                    <MdLogin />
                  </span>

                  Rider Login
                </button>
              </div>

              {/* Trust */}
              <div className="mt-9 flex flex-wrap items-center gap-x-7 gap-y-4">
                <div className="flex items-center gap-3">
                  <div className="flex -space-x-2">
                    <span className="h-9 w-9 rounded-full border-2 border-white bg-neutral-200" />
                    <span className="h-9 w-9 rounded-full border-2 border-white bg-red-200" />
                    <span className="h-9 w-9 rounded-full border-2 border-white bg-orange-200" />
                    <span className="h-9 w-9 rounded-full border-2 border-white bg-neutral-300" />
                  </div>

                  <div>
                    <div className="flex items-center gap-1">
                      <FaStar className="text-xs text-orange-400" />

                      <span className="text-sm font-black text-neutral-800">
                        Rider Community
                      </span>
                    </div>

                    <p className="text-xs text-neutral-400">
                      Growing every day
                    </p>
                  </div>
                </div>

                <div className="hidden h-8 w-px bg-neutral-200 sm:block" />

                <div className="flex items-center gap-2">
                  <FaCheckCircle className="text-lg text-green-500" />

                  <span className="text-sm font-bold text-neutral-600">
                    Easy Registration
                  </span>
                </div>
              </div>
            </div>

            {/* =====================================================
                UNIQUE HERO VISUAL
            ===================================================== */}
            <div className="relative z-10">
              {/* Large Red Shape */}
              <div className="absolute right-0 top-1/2 h-[430px] w-[430px] -translate-y-1/2 rounded-full bg-red-500/10 blur-3xl" />

              <div className="relative mx-auto max-w-[610px]">
                {/* Decorative Ring */}
                <div className="absolute -right-8 -top-8 h-36 w-36 rounded-full border-[22px] border-red-100/80 sm:-right-12 sm:-top-12 sm:h-44 sm:w-44" />

                {/* Main Card */}
                <div className="relative overflow-hidden rounded-[38px] border border-neutral-100 bg-neutral-950 p-5 shadow-[0_35px_100px_rgba(0,0,0,0.16)] sm:p-7">
                  {/* Card Top */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-red-500 text-white shadow-lg">
                        <MdDeliveryDining className="text-2xl" />
                      </div>

                      <div>
                        <p className="text-xs font-semibold text-neutral-500">
                          Foodbite24
                        </p>

                        <p className="text-sm font-black text-white">
                          Rider Dashboard
                        </p>
                      </div>
                    </div>

                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/5 text-neutral-300">
                      <MdNotifications />
                    </div>
                  </div>

                  {/* Earnings */}
                  <div className="mt-6 rounded-[28px] bg-gradient-to-br from-red-500 to-red-600 p-6 text-white shadow-[0_20px_45px_rgba(239,68,68,0.22)]">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-xs font-bold text-red-100">
                          Today's Earnings
                        </p>

                        <p className="mt-2 text-4xl font-black tracking-tight">
                          ৳ 1,850
                        </p>

                        <div className="mt-3 flex items-center gap-2 text-xs font-bold text-red-100">
                          <FaCheckCircle />
                          Earnings updated
                        </div>
                      </div>

                      <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/15">
                        <FaWallet className="text-lg" />
                      </div>
                    </div>
                  </div>

                  {/* Dashboard Grid */}
                  <div className="mt-4 grid grid-cols-2 gap-4">
                    <div className="rounded-[24px] border border-white/10 bg-white/[0.06] p-5">
                      <div className="flex items-center justify-between">
                        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-red-500/15 text-red-400">
                          <MdDeliveryDining />
                        </div>

                        <span className="text-[10px] font-bold uppercase tracking-wider text-neutral-500">
                          Today
                        </span>
                      </div>

                      <p className="mt-4 text-3xl font-black text-white">
                        18
                      </p>

                      <p className="mt-1 text-xs font-semibold text-neutral-500">
                        Deliveries
                      </p>
                    </div>

                    <div className="rounded-[24px] border border-white/10 bg-white/[0.06] p-5">
                      <div className="flex items-center justify-between">
                        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-orange-500/15 text-orange-400">
                          <FaClock />
                        </div>

                        <span className="text-[10px] font-bold uppercase tracking-wider text-neutral-500">
                          Active
                        </span>
                      </div>

                      <p className="mt-4 text-3xl font-black text-white">
                        7.5h
                      </p>

                      <p className="mt-1 text-xs font-semibold text-neutral-500">
                        Working Time
                      </p>
                    </div>
                  </div>

                  {/* Active Delivery */}
                  <div className="mt-4 rounded-[24px] border border-white/10 bg-white/[0.06] p-5">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-red-500/15 text-red-400">
                          <MdLocationOn className="text-xl" />
                        </div>

                        <div>
                          <p className="text-sm font-black text-white">
                            Active Delivery
                          </p>

                          <p className="mt-1 text-xs font-semibold text-neutral-500">
                            Restaurant → Customer
                          </p>
                        </div>
                      </div>

                      <span className="flex items-center gap-2 rounded-full bg-green-500/10 px-3 py-1.5 text-[10px] font-black text-green-400">
                        <span className="h-1.5 w-1.5 rounded-full bg-green-400" />
                        ACTIVE
                      </span>
                    </div>

                    <div className="mt-5 h-2 overflow-hidden rounded-full bg-white/10">
                      <div className="h-full w-[72%] rounded-full bg-red-500" />
                    </div>

                    <div className="mt-3 flex items-center justify-between text-[10px] font-bold text-neutral-500">
                      <span>Pickup completed</span>
                      <span>72%</span>
                    </div>
                  </div>
                </div>

                {/* Floating Earnings */}
                <div className="absolute -left-8 bottom-10 hidden rounded-2xl border border-neutral-100 bg-white p-4 shadow-[0_20px_50px_rgba(0,0,0,0.13)] sm:block">
                  <div className="flex items-center gap-3">
                    <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-green-50 text-green-600">
                      <FaMoneyBillWave />
                    </div>

                    <div>
                      <p className="text-[10px] font-bold text-neutral-400">
                        Total Earnings
                      </p>

                      <p className="text-lg font-black text-neutral-900">
                        ৳ 12,480
                      </p>
                    </div>
                  </div>
                </div>

                {/* Floating Rider Status */}
                <div className="absolute -right-7 bottom-24 hidden rounded-2xl border border-neutral-100 bg-white p-4 shadow-[0_20px_50px_rgba(0,0,0,0.13)] sm:block">
                  <div className="flex items-center gap-3">
                    <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-red-50 text-red-500">
                      <FaMotorcycle />
                    </div>

                    <div>
                      <p className="text-[10px] font-bold text-neutral-400">
                        Rider Status
                      </p>

                      <div className="mt-1 flex items-center gap-2">
                        <span className="h-2 w-2 rounded-full bg-green-500" />

                        <p className="text-xs font-black text-neutral-900">
                          Available
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Hero Bottom Strip */}
          <div className="mt-16 grid overflow-hidden rounded-[28px] border border-neutral-100 bg-white shadow-[0_20px_70px_rgba(0,0,0,0.06)] sm:grid-cols-3">
            <div className="flex items-center gap-4 border-b border-neutral-100 p-6 sm:border-b-0 sm:border-r">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-red-50 text-red-500">
                <FaStore />
              </div>

              <div>
                <p className="text-xl font-black text-neutral-900">100+</p>

                <p className="text-xs font-semibold text-neutral-400">
                  Partner Businesses
                </p>
              </div>
            </div>

            <div className="flex items-center gap-4 border-b border-neutral-100 p-6 sm:border-b-0 sm:border-r">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-orange-50 text-orange-500">
                <MdDeliveryDining />
              </div>

              <div>
                <p className="text-xl font-black text-neutral-900">Daily</p>

                <p className="text-xs font-semibold text-neutral-400">
                  Delivery Opportunities
                </p>
              </div>
            </div>

            <div className="flex items-center gap-4 p-6">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-green-50 text-green-600">
                <MdSupportAgent />
              </div>

              <div>
                <p className="text-xl font-black text-neutral-900">24/7</p>

                <p className="text-xs font-semibold text-neutral-400">
                  Rider Support
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
            <Register/>
      {/* =========================================================
          BENEFITS
      ========================================================= */}
      <section
        id="benefits"
        className="relative overflow-hidden bg-neutral-50 px-4 py-24 sm:px-6 lg:px-8 lg:py-32"
      >
        <div className="pointer-events-none absolute right-[-150px] top-10 h-96 w-96 rounded-full bg-red-100/50 blur-3xl" />

        <div className="pointer-events-none absolute bottom-[-150px] left-[-150px] h-96 w-96 rounded-full bg-orange-100/50 blur-3xl" />

        <div className="relative mx-auto max-w-7xl">
          <div className="mx-auto max-w-2xl text-center">
            <span className="inline-flex rounded-full bg-red-50 px-4 py-2 text-xs font-black uppercase tracking-[0.16em] text-red-500">
              Why Foodbite24
            </span>

            <h2 className="mt-5 text-3xl font-black tracking-[-0.035em] text-neutral-950 sm:text-4xl lg:text-5xl">
              Built for riders who want
              <span className="text-red-500"> more flexibility.</span>
            </h2>

            <p className="mt-5 text-base leading-8 text-neutral-500">
              Everything you need to make your delivery journey simple,
              flexible and easier to manage.
            </p>
          </div>

          <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {benefits.map((item, index) => (
              <div
                key={index}
                className="group relative overflow-hidden rounded-[26px] border border-neutral-100 bg-white p-7 shadow-[0_12px_40px_rgba(0,0,0,0.04)] transition duration-300 hover:-translate-y-2 hover:shadow-[0_25px_60px_rgba(0,0,0,0.08)]"
              >
                <div className="absolute right-0 top-0 h-24 w-24 translate-x-8 -translate-y-8 rounded-full bg-red-50 transition duration-300 group-hover:bg-red-100" />

                <div className="relative flex h-14 w-14 items-center justify-center rounded-2xl bg-red-50 text-xl text-red-500 transition duration-300 group-hover:bg-red-500 group-hover:text-white">
                  {item.icon}
                </div>

                <h3 className="relative mt-6 text-lg font-black text-neutral-900">
                  {item.title}
                </h3>

                <p className="relative mt-3 text-sm leading-7 text-neutral-500">
                  {item.text}
                </p>

                <div className="relative mt-6 flex items-center gap-2 text-xs font-black text-red-500">
                  Explore Benefit
                  <FaChevronRight className="text-[9px]" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* =========================================================
          DASHBOARD
      ========================================================= */}
      <section className="relative overflow-hidden bg-white px-4 py-24 sm:px-6 lg:px-8 lg:py-32">
        <div className="mx-auto grid max-w-7xl items-center gap-14 lg:grid-cols-2">
          {/* Dashboard */}
          <div className="relative order-2 lg:order-1">
            <div className="absolute -inset-5 rounded-[40px] bg-red-50 blur-2xl" />

            <div className="relative overflow-hidden rounded-[30px] border border-neutral-100 bg-neutral-50 p-4 shadow-[0_30px_80px_rgba(0,0,0,0.10)] sm:p-6">
              <div className="rounded-[24px] bg-white p-5 shadow-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-bold text-neutral-400">
                      Good Morning
                    </p>

                    <h3 className="mt-1 text-xl font-black text-neutral-900">
                      Rider Dashboard
                    </h3>
                  </div>

                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-red-50 text-red-500">
                    <MdNotifications className="text-xl" />
                  </div>
                </div>

                <div className="mt-6 rounded-2xl bg-red-500 p-5 text-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-bold text-red-100">
                        Total Earnings
                      </p>

                      <p className="mt-2 text-3xl font-black">৳ 12,480</p>
                    </div>

                    <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-white/15">
                      <FaWallet />
                    </div>
                  </div>

                  <div className="mt-4 flex items-center gap-2 text-xs font-bold text-red-100">
                    <FaCheckCircle />
                    Earnings updated
                  </div>
                </div>

                <div className="mt-5 grid grid-cols-2 gap-3">
                  <div className="rounded-2xl border border-neutral-100 bg-neutral-50 p-4">
                    <div className="flex items-center gap-2">
                      <MdDeliveryDining className="text-red-500" />

                      <span className="text-xs font-bold text-neutral-400">
                        Today
                      </span>
                    </div>

                    <p className="mt-2 text-2xl font-black text-neutral-900">
                      18
                    </p>

                    <p className="text-xs font-semibold text-neutral-400">
                      Deliveries
                    </p>
                  </div>

                  <div className="rounded-2xl border border-neutral-100 bg-neutral-50 p-4">
                    <div className="flex items-center gap-2">
                      <FaClock className="text-orange-500" />

                      <span className="text-xs font-bold text-neutral-400">
                        Active
                      </span>
                    </div>

                    <p className="mt-2 text-2xl font-black text-neutral-900">
                      04
                    </p>

                    <p className="text-xs font-semibold text-neutral-400">
                      Orders
                    </p>
                  </div>
                </div>

                <div className="mt-5 rounded-2xl border border-neutral-100 bg-white p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-red-50 text-red-500">
                        <FaStore />
                      </div>

                      <div>
                        <p className="text-sm font-black text-neutral-900">
                          New Delivery
                        </p>

                        <p className="text-xs font-semibold text-neutral-400">
                          Restaurant Order #FB24018
                        </p>
                      </div>
                    </div>

                    <span className="rounded-full bg-green-50 px-3 py-1 text-[10px] font-black text-green-600">
                      ACTIVE
                    </span>
                  </div>

                  <div className="mt-4 flex items-center gap-2 text-xs font-semibold text-neutral-500">
                    <MdLocationOn className="text-red-500" />

                    Delivery location available
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="order-1 lg:order-2">
            <span className="inline-flex rounded-full bg-red-50 px-4 py-2 text-xs font-black uppercase tracking-[0.16em] text-red-500">
              Smart Rider Dashboard
            </span>

            <h2 className="mt-5 text-3xl font-black leading-tight tracking-[-0.035em] text-neutral-950 sm:text-4xl lg:text-5xl">
              Everything you need,
              <span className="block text-red-500">in one place.</span>
            </h2>

            <p className="mt-6 text-base leading-8 text-neutral-500">
              Manage orders, deliveries, earnings and rider activity through a
              clean and simple dashboard built around your daily work.
            </p>

            <div className="mt-8 space-y-4">
              {riderFeatures.map((feature, index) => (
                <div key={index} className="flex items-center gap-3">
                  <FaCheckCircle className="shrink-0 text-green-500" />

                  <span className="text-sm font-bold text-neutral-700">
                    {feature}
                  </span>
                </div>
              ))}
            </div>

            <div className="mt-9">
              <button
                type="button"
                onClick={goRegister}
                className="group relative z-[100] inline-flex min-h-[54px] cursor-pointer items-center justify-center gap-3 rounded-2xl bg-red-500 px-6 text-sm font-black text-white shadow-[0_16px_35px_rgba(239,68,68,0.24)] transition duration-300 hover:-translate-y-1 hover:bg-red-600 active:scale-[0.98]"
              >
                Start Your Application

                <FaArrowRight className="transition duration-300 group-hover:translate-x-1" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* =========================================================
          RIDER EXPERIENCE
      ========================================================= */}
      <section className="relative overflow-hidden bg-neutral-50 px-4 py-24 sm:px-6 lg:px-8 lg:py-32">
        <div className="pointer-events-none absolute left-[-180px] top-20 h-[420px] w-[420px] rounded-full bg-red-100/50 blur-3xl" />

        <div className="relative mx-auto grid max-w-7xl items-center gap-14 lg:grid-cols-2">
          <div>
            <span className="inline-flex rounded-full bg-red-50 px-4 py-2 text-xs font-black uppercase tracking-[0.16em] text-red-500">
              Rider Experience
            </span>

            <h2 className="mt-5 text-3xl font-black leading-tight tracking-[-0.035em] text-neutral-950 sm:text-4xl lg:text-5xl">
              Make every delivery
              <span className="block text-red-500">feel effortless.</span>
            </h2>

            <p className="mt-6 max-w-xl text-base leading-8 text-neutral-500">
              Your focus should be on delivering great service. Foodbite24
              keeps the rider experience simple with clear updates and easy
              earning tracking.
            </p>

            <div className="mt-8 grid gap-4 sm:grid-cols-2">
              <div className="rounded-2xl border border-neutral-100 bg-white p-5">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-red-50 text-red-500">
                  <MdNotifications />
                </div>

                <h3 className="mt-4 text-sm font-black text-neutral-900">
                  Instant Updates
                </h3>

                <p className="mt-2 text-xs leading-6 text-neutral-500">
                  Stay informed about new orders and delivery updates.
                </p>
              </div>

              <div className="rounded-2xl border border-neutral-100 bg-white p-5">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-green-50 text-green-600">
                  <FaWallet />
                </div>

                <h3 className="mt-4 text-sm font-black text-neutral-900">
                  Track Earnings
                </h3>

                <p className="mt-2 text-xs leading-6 text-neutral-500">
                  Keep track of your earnings and delivery activity.
                </p>
              </div>
            </div>

            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <button
                type="button"
                onClick={goRegister}
                className="group relative z-[100] inline-flex min-h-[54px] cursor-pointer items-center justify-center gap-3 rounded-2xl bg-red-500 px-6 text-sm font-black text-white shadow-[0_16px_35px_rgba(239,68,68,0.25)] transition hover:-translate-y-1 hover:bg-red-600"
              >
                Start Your Application

                <FaArrowRight className="transition group-hover:translate-x-1" />
              </button>

              <a
                href="tel:+8801336116868"
                className="inline-flex min-h-[54px] items-center justify-center gap-3 rounded-2xl border border-neutral-200 bg-white px-6 text-sm font-bold text-neutral-700 transition hover:-translate-y-1 hover:border-red-200 hover:bg-red-50 hover:text-red-600"
              >
                <FaPhoneAlt className="text-red-500" />
                Rider Support
              </a>
            </div>
          </div>

          {/* Phone UI */}
          <div className="relative flex justify-center">
            <div className="absolute h-80 w-80 rounded-full bg-red-100 blur-3xl" />

            <div className="relative w-[260px] rounded-[38px] border-[8px] border-neutral-900 bg-white p-2 shadow-[0_35px_90px_rgba(0,0,0,0.18)] sm:w-[290px]">
              <div className="overflow-hidden rounded-[30px] bg-neutral-50">
                <div className="flex items-center justify-between bg-red-500 px-5 py-5 text-white">
                  <div>
                    <p className="text-[10px] font-bold text-red-100">
                      Welcome back
                    </p>

                    <p className="text-sm font-black">Rider</p>
                  </div>

                  <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-white/15">
                    <MdNotifications />
                  </div>
                </div>

                <div className="p-4">
                  <div className="rounded-2xl bg-white p-4 shadow-sm">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-bold text-neutral-400">
                        Today's Earnings
                      </span>

                      <FaWallet className="text-sm text-red-500" />
                    </div>

                    <p className="mt-2 text-2xl font-black text-neutral-900">
                      ৳ 1,850
                    </p>

                    <div className="mt-3 h-2 overflow-hidden rounded-full bg-red-50">
                      <div className="h-full w-[72%] rounded-full bg-red-500" />
                    </div>
                  </div>

                  <div className="mt-3 grid grid-cols-2 gap-3">
                    <div className="rounded-2xl bg-white p-4 shadow-sm">
                      <MdDeliveryDining className="text-lg text-red-500" />

                      <p className="mt-2 text-xl font-black">18</p>

                      <p className="text-[9px] font-bold text-neutral-400">
                        Deliveries
                      </p>
                    </div>

                    <div className="rounded-2xl bg-white p-4 shadow-sm">
                      <FaClock className="text-lg text-orange-500" />

                      <p className="mt-2 text-xl font-black">7.5h</p>

                      <p className="text-[9px] font-bold text-neutral-400">
                        Active Time
                      </p>
                    </div>
                  </div>

                  <div className="mt-3 rounded-2xl bg-white p-4 shadow-sm">
                    <div className="flex items-center gap-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-red-50 text-red-500">
                        <MdLocationOn />
                      </div>

                      <div>
                        <p className="text-xs font-black text-neutral-900">
                          Active Delivery
                        </p>

                        <p className="mt-1 text-[9px] font-semibold text-neutral-400">
                          3.2 km away
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-3 rounded-2xl bg-white p-4 shadow-sm">
                    <div className="flex items-center gap-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-green-50 text-green-600">
                        <MdPayments />
                      </div>

                      <div>
                        <p className="text-xs font-black text-neutral-900">
                          Payment
                        </p>

                        <p className="mt-1 text-[9px] font-semibold text-green-600">
                          Updated successfully
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* =========================================================
          HOW IT WORKS
      ========================================================= */}
      <section
        id="how-it-works"
        className="bg-white px-4 py-24 sm:px-6 lg:px-8 lg:py-32"
      >
        <div className="mx-auto max-w-7xl">
          <div className="mx-auto max-w-2xl text-center">
            <span className="inline-flex rounded-full bg-red-50 px-4 py-2 text-xs font-black uppercase tracking-[0.16em] text-red-500">
              Simple Process
            </span>

            <h2 className="mt-5 text-3xl font-black tracking-[-0.035em] text-neutral-950 sm:text-4xl lg:text-5xl">
              Start your rider journey
              <span className="text-red-500"> in 3 simple steps.</span>
            </h2>

            <p className="mt-5 text-base leading-8 text-neutral-500">
              Complete a few simple steps and become part of the Foodbite24
              Rider community.
            </p>
          </div>

          <div className="relative mt-16 grid gap-8 lg:grid-cols-3">
            <div className="pointer-events-none absolute left-[18%] right-[18%] top-12 hidden h-px border-t border-dashed border-red-200 lg:block" />

            {steps.map((step, index) => (
              <div
                key={index}
                className="relative rounded-[28px] border border-neutral-100 bg-neutral-50 p-7 text-center transition duration-300 hover:-translate-y-2 hover:bg-white hover:shadow-[0_25px_60px_rgba(0,0,0,0.07)]"
              >
                <div className="relative mx-auto flex h-20 w-20 items-center justify-center rounded-[24px] bg-red-500 text-2xl text-white shadow-[0_15px_35px_rgba(239,68,68,0.25)]">
                  {step.icon}

                  <span className="absolute -right-2 -top-2 flex h-7 w-7 items-center justify-center rounded-full border-4 border-neutral-50 bg-neutral-900 text-[9px] font-black text-white">
                    {step.number}
                  </span>
                </div>

                <h3 className="mt-6 text-lg font-black text-neutral-900">
                  {step.title}
                </h3>

                <p className="mx-auto mt-3 max-w-xs text-sm leading-7 text-neutral-500">
                  {step.text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* =========================================================
          SUPPORT
      ========================================================= */}
      <section
        id="support"
        className="relative overflow-hidden px-4 py-24 sm:px-6 lg:px-8"
      >
        <div className="absolute inset-0 bg-neutral-950" />

        <div className="pointer-events-none absolute -left-20 top-[-150px] h-96 w-96 rounded-full bg-red-500/20 blur-3xl" />

        <div className="pointer-events-none absolute -right-20 bottom-[-150px] h-96 w-96 rounded-full bg-orange-500/10 blur-3xl" />

        <div className="relative mx-auto max-w-7xl">
          <div className="grid gap-10 lg:grid-cols-[1fr_auto] lg:items-center">
            <div>
              <span className="inline-flex rounded-full border border-red-400/20 bg-red-500/10 px-4 py-2 text-xs font-black uppercase tracking-[0.16em] text-red-400">
                Rider Support
              </span>

              <h2 className="mt-5 max-w-3xl text-3xl font-black leading-tight tracking-[-0.035em] text-white sm:text-4xl lg:text-5xl">
                Need help?
                <span className="block text-red-400">
                  We are here for you.
                </span>
              </h2>

              <p className="mt-5 max-w-2xl text-base leading-8 text-neutral-400">
                If you need support with rider registration, deliveries or
                earnings, our support team is ready to help.
              </p>
            </div>

            <a
              href="tel:+8801336116868"
              className="group inline-flex min-h-[60px] items-center justify-center gap-3 rounded-2xl bg-red-500 px-7 text-sm font-black text-white shadow-[0_18px_45px_rgba(239,68,68,0.25)] transition duration-300 hover:-translate-y-1 hover:bg-red-600"
            >
              <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-white/15">
                <FaPhoneAlt />
              </span>

              +880 1336-116868

              <FaArrowRight className="transition group-hover:translate-x-1" />
            </a>
          </div>
        </div>
      </section>

      {/* =========================================================
          FINAL CTA
      ========================================================= */}
      <section className="relative overflow-hidden bg-red-500 px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute -left-32 top-[-120px] h-80 w-80 rounded-full bg-white/10 blur-3xl" />

          <div className="absolute -right-32 bottom-[-120px] h-80 w-80 rounded-full bg-black/10 blur-3xl" />

          <div className="absolute left-1/2 top-1/2 h-60 w-60 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white/5 blur-3xl" />
        </div>

        <div className="relative mx-auto max-w-4xl text-center">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-white/15 text-3xl text-white backdrop-blur-md">
            <FaMotorcycle />
          </div>

          <h2 className="mt-7 text-3xl font-black leading-tight tracking-[-0.04em] text-white sm:text-4xl lg:text-5xl">
            Ready to ride with Foodbite24?
          </h2>

          <p className="mx-auto mt-5 max-w-2xl text-sm leading-7 text-red-100 sm:text-base">
            Start your rider journey today and create flexible earning
            opportunities on your own schedule.
          </p>

          <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
            <button
              type="button"
              onClick={goRegister}
              className="group relative z-[100] flex min-h-[56px] cursor-pointer items-center justify-center gap-3 rounded-2xl bg-white px-7 text-sm font-black text-red-600 shadow-xl transition duration-300 hover:-translate-y-1 hover:bg-red-50 active:scale-[0.98]"
            >
              Join as Rider

              <FaArrowRight className="transition group-hover:translate-x-1" />
            </button>

            <button
              type="button"
              onClick={goLogin}
              className="relative z-[100] flex min-h-[56px] cursor-pointer items-center justify-center gap-3 rounded-2xl border border-white/25 bg-white/10 px-7 text-sm font-bold text-white backdrop-blur-md transition duration-300 hover:-translate-y-1 hover:bg-white/15 active:scale-[0.98]"
            >
              <MdLogin className="text-lg" />

              Rider Login
            </button>
          </div>
        </div>
      </section>

      {/* =========================================================
          FOOTER
      ========================================================= */}
        <Footer/>
    </main>
  );
};

export default Home;